import { Component, signal, computed, inject, ViewChild, ChangeDetectionStrategy, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl } from '@angular/forms';
import { DatepickerComponent } from '../../../../shared/components/ui/datepicker/datepicker';
import { SidePanelComponent, SidePanelConfig } from '../../../../shared/components/ui/side-panel/side-panel';
import { ButtonComponent } from '../../../../shared/components/ui/button/button';
import { AuthService } from '../../../../core/services/auth.service';
import { ContactarUtentePanelService } from '../../../../core/services/contactar-utente-panel.service';
import { MatContactLogService } from '../../../../core/services/mat-contact-log.service';
import { TeamMemberService } from '../../../../core/services/team-member.service';
import { TaskRepository } from '../../../../core/repositories/task.repository';
import { NotificationService } from '../../../../shared/services/notification.service';
import { environment } from '../../../../../environments/environment';
import {
  CreateMatContactLogPayload,
  MatContactChannel,
  MatContactOutcome,
  MAT_CONTACT_CHANNEL_OPTIONS,
  MAT_CONTACT_OUTCOME_OPTIONS
} from '../../../../core/models/mat-contact-log.model';

interface GuiaoStep {
  readonly key: string;
  readonly title: string;
  readonly bullets: ReadonlyArray<string>;
}

const GUIAO_STEPS: ReadonlyArray<GuiaoStep> = [
  {
    key: 'enquadrar',
    title: '1. Enquadrar o contacto',
    bullets: [
      'Apresente-se, identifique a farmácia e confirme que está a falar com o utente (ou cuidador responsável).',
      'Explique o motivo do contacto: "Estamos a acompanhar a sua medicação crónica para garantir que está tudo bem."'
    ]
  },
  {
    key: 'situacao-atual',
    title: '2. Perceber a situação atual',
    bullets: [
      'Pergunte se o utente continua a tomar a medicação tal como foi prescrita.',
      'Confirme se houve alguma alteração recente: nova receita, suspensão por indicação médica, mudança de dose.'
    ]
  },
  {
    key: 'experiencia',
    title: '3. Explorar a experiência',
    bullets: [
      'Pergunte se está a sentir melhorias, dificuldades, ou efeitos secundários.',
      'Identifique barreiras: esquecimento, custo, complexidade do esquema, dúvidas sobre a necessidade.'
    ]
  },
  {
    key: 'nao-adesao',
    title: '4. Aprofundar não-adesão (se aplicável)',
    bullets: [
      'Se o utente admite que não está a tomar conforme prescrito, valide o motivo concreto.',
      'Reforce o impacto da adesão. Pergunte como pode ajudar: lembretes, encaminhar para o médico, mudança de horário.'
    ]
  },
  {
    key: 'fecho',
    title: '5. Fechar a chamada',
    bullets: [
      'Resuma o combinado e agradeça o tempo do utente.',
      'Indique o próximo passo (nova receita, follow-up, dispensa).'
    ]
  }
];

@Component({
  selector: 'app-contactar-utente-panel',
  standalone: true,
  imports: [CommonModule, SidePanelComponent, ButtonComponent, DatepickerComponent],
  templateUrl: './contactar-utente-panel.html',
  styleUrl: './contactar-utente-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContactarUtentePanelComponent {
  private readonly authService = inject(AuthService);
  private readonly panelService = inject(ContactarUtentePanelService);
  private readonly contactsService = inject(MatContactLogService);
  private readonly teamMemberService = inject(TeamMemberService);
  private readonly taskRepository = inject(TaskRepository);
  private readonly notification = inject(NotificationService);

  @ViewChild(SidePanelComponent) private sidePanel?: SidePanelComponent;

  // Read state from the shared service
  readonly isOpen = this.panelService.isOpen;
  readonly isMinimized = this.panelService.isMinimized;
  private readonly accountId = this.panelService.accountId;
  private readonly patientName = this.panelService.patientName;
  private readonly primaryContactId = this.panelService.primaryContactId;
  readonly medications = this.panelService.medications;
  // Constants for template
  readonly channelOptions = MAT_CONTACT_CHANNEL_OPTIONS;
  readonly outcomeOptions = MAT_CONTACT_OUTCOME_OPTIONS;
  readonly guiaoSteps = GUIAO_STEPS;

  // Team members
  readonly teamMembers = signal<{ id: string; name: string }[]>([]);
  readonly selectedTeamMemberId = signal<string>('');

  // Form state
  readonly channel = signal<MatContactChannel | ''>('');
  readonly outcome = signal<MatContactOutcome | ''>('');
  readonly observations = signal<string>('');
  readonly isSaving = signal<boolean>(false);
  readonly serverError = signal<string | null>(null);

  // Stepper state
  readonly currentStepIndex = signal<number>(0);

  // Follow-up form state
  readonly showFollowUp = signal<boolean>(false);
  readonly followUpSubject = signal<string>('');
  readonly followUpDate = signal<string>('');
  readonly followUpDateControl = new FormControl('');
  readonly followUpMedicationId = signal<string>('');
  readonly followUpTeamMemberId = signal<string>('');
  readonly followUpNotes = signal<string>('');
  readonly isFollowUpSaving = signal<boolean>(false);
  readonly followUpError = signal<string | null>(null);
  readonly todayDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD for min attr

  // Convenience reads
  readonly currentUserName = computed(() => this.authService.profileName() || '—');
  readonly isValid = computed(() => this.channel() !== '' && this.outcome() !== '' && this.selectedTeamMemberId() !== '');
  readonly isFollowUpValid = computed(() => this.followUpSubject().trim().length > 0 && this.followUpDate() !== '');

  /** True when the user has entered any data in the form fields. */
  readonly isDirty = computed(() =>
    this.channel() !== '' || this.outcome() !== '' || this.observations().trim().length > 0
    || this.followUpSubject().trim().length > 0 || this.followUpDate() !== '' || this.followUpNotes().trim().length > 0
  );

  constructor() {
    this.followUpDateControl.valueChanges.subscribe(v => {
      if (v) {
        const d = new Date(v);
        if (!isNaN(d.getTime())) {
          this.followUpDate.set(d.toISOString().split('T')[0]);
        }
      } else {
        this.followUpDate.set('');
      }
    });

    effect(() => {
      if (this.isOpen() && this.teamMembers().length === 0) {
        this.teamMemberService.getTeamMembers({ take: 100, orderBy: 'name', orderDirection: 'asc', includePicklistLabels: false }).subscribe({
          next: res => {
            const members = (res.records || [])
              .filter((m: any) => !m.desativado__c && !m.Desativado)
              .map((m: any) => ({ id: m.id, name: m.name || m.Name || '—' }));
            this.teamMembers.set(members);
          },
          error: () => this.teamMembers.set([])
        });
      }
    });
  }

  readonly sidePanelConfig = computed<SidePanelConfig>(() => ({
    title: 'Contactar utente',
    subtitle: this.patientName() || undefined,
    icon: 'call',
    width: '420px',
    showCloseButton: true,
    closeOnEscape: !this.isSaving() && !this.isFollowUpSaving()
  }));

  // ── Stepper ─────────────────────────────────────────────────────────────

  goToStep(index: number): void {
    if (index >= 0 && index < this.guiaoSteps.length) {
      this.currentStepIndex.set(index);
    }
  }

  // ── Follow-up ───────────────────────────────────────────────────────────

  toggleFollowUp(): void {
    const opening = !this.showFollowUp();
    this.showFollowUp.set(opening);
    if (opening && this.followUpSubject() === '') {
      this.followUpSubject.set(`Lembrete: Follow-up MAT - ${this.patientName() || 'utente'}`);
    }
  }

  onFollowUpSubjectInput(value: string): void {
    this.followUpSubject.set(value);
    this.followUpError.set(null);
  }

  onFollowUpDateInput(value: string): void {
    this.followUpDate.set(value);
    this.followUpError.set(null);
  }

  onFollowUpNotesInput(value: string): void {
    this.followUpNotes.set(value);
    this.followUpError.set(null);
  }

  onFollowUpSave(): void {
    if (!this.isFollowUpValid() || this.isFollowUpSaving()) return;

    const accountId = this.accountId();
    if (!accountId) {
      this.followUpError.set('Não foi possível identificar o utente.');
      return;
    }

    this.isFollowUpSaving.set(true);
    this.followUpError.set(null);

    const payload: Record<string, unknown> = {
      subject: this.followUpSubject().trim(),
      activityDate: this.followUpDate(),
      whatId: accountId,
      status: 'Open',
      recordTypeId: environment.recordTypes.trackingCompliance
    };

    const contactId = this.primaryContactId();
    if (contactId) {
      payload['RelatedContact__c'] = contactId;
    }

    const medId = this.followUpMedicationId();
    if (medId) {
      payload['Related_Chronic_Medication__c'] = medId;
    }

    const notes = this.followUpNotes().trim();
    if (notes.length > 0) {
      payload['description'] = notes;
    }

    const tmId = this.followUpTeamMemberId();
    if (tmId) {
      payload['Team_Member__c'] = tmId;
    }

    this.taskRepository.createTask(payload).subscribe({
      next: () => {
        this.isFollowUpSaving.set(false);
        this.notification.showSuccess('Lembrete criado com sucesso.', 4000);
        this.resetFollowUp();
      },
      error: err => {
        this.isFollowUpSaving.set(false);
        const message =
          err?.error?.error ??
          err?.error?.message ??
          'Erro ao criar tarefa de follow-up. Tente novamente.';
        this.followUpError.set(message);
        this.notification.showError(message);
      }
    });
  }

  private resetFollowUp(): void {
    this.showFollowUp.set(false);
    this.followUpSubject.set('');
    this.followUpDate.set('');
    this.followUpDateControl.reset();
    this.followUpNotes.set('');
    this.followUpMedicationId.set('');
    this.followUpTeamMemberId.set('');
    this.followUpError.set(null);
  }

  // ── Form handlers ───────────────────────────────────────────────────────

  onChannelChange(value: MatContactChannel): void {
    this.channel.set(value);
    this.serverError.set(null);
  }

  onOutcomeChange(value: MatContactOutcome): void {
    this.outcome.set(value);
    this.serverError.set(null);
  }

  onObservationsInput(value: string): void {
    this.observations.set(value);
    this.serverError.set(null);
  }

  /** Called by the side panel's panelClosed output (after guard confirmed). */
  onClose(): void {
    if (this.isSaving()) return;
    this.resetForm();
    this.panelService.close();
  }

  /** Called by the Cancelar button — delegates to the side panel's unsaved-changes guard. */
  onCancelClick(): void {
    if (this.isSaving()) return;
    this.sidePanel?.requestClose();
  }

  onMinimize(): void {
    this.panelService.minimize();
  }

  onRestore(): void {
    this.panelService.restore();
  }

  onSave(): void {
    if (!this.isValid() || this.isSaving()) return;

    const accountId = this.accountId();
    if (!accountId) {
      this.serverError.set('Não foi possível identificar o utente.');
      return;
    }

    const payload: CreateMatContactLogPayload = {
      channel: this.channel() as MatContactChannel,
      outcome: this.outcome() as MatContactOutcome
    };

    const obs = this.observations().trim();
    if (obs.length > 0) {
      payload.observations = obs;
    }

    const teamMemberId = this.selectedTeamMemberId();
    if (teamMemberId) {
      payload.teamMemberId = teamMemberId;
    }

    this.isSaving.set(true);
    this.serverError.set(null);

    this.contactsService.createContact(accountId, payload).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.notification.showSuccess(
          'Contacto registado. De acordo com a informação obtida, realize as ações necessárias na linha de cada medicamento.',
          5000
        );
        this.resetForm();
        this.panelService.notifySaved();
        this.panelService.close();
      },
      error: err => {
        this.isSaving.set(false);
        const message =
          err?.error?.error ??
          err?.error?.message ??
          'Erro ao registar contacto. Tente novamente.';
        this.serverError.set(message);
        this.notification.showError(message);
      }
    });
  }

  private resetForm(): void {
    this.channel.set('');
    this.outcome.set('');
    this.observations.set('');
    this.selectedTeamMemberId.set('');
    this.serverError.set(null);
    this.currentStepIndex.set(0);
    this.resetFollowUp();
  }
}
